import React, { useState, useCallback, useEffect } from 'react';
import { Save } from 'lucide-react';
import { TranscriptionSegment } from '@/hooks/useTranscription';
import { SegmentRow } from './SegmentRow';
import { FormatExporter } from './FormatExporter';

interface SubtitleEditorProps {
  segments: TranscriptionSegment[];
  onChange: (segments: TranscriptionSegment[]) => void;
  onSave?: () => void;
  filename?: string;
  saving?: boolean;
  disabled?: boolean;
}

/**
 * 字幕編輯器
 * 顯示所有段落列表，支援編輯、刪除、合併
 */
export function SubtitleEditor({
  segments,
  onChange,
  onSave,
  filename,
  saving,
  disabled,
}: SubtitleEditorProps) {
  const [hasChanges, setHasChanges] = useState(false);

  // 當外部傳入的 segments 發生變化且長度不為 0 時，重置變更狀態（通常是切換錄音或剛生成完畢）
  // 這樣生成的字幕如果是自動儲存的，就不會顯示「未儲存變更」
  useEffect(() => {
    setHasChanges(false);
  }, [segments.length]);

  // 更新單一段落
  const handleSegmentChange = useCallback((index: number, updates: Partial<TranscriptionSegment>) => {
    const newSegments = segments.map((seg, i) =>
      i === index ? { ...seg, ...updates } : seg
    );
    onChange(newSegments);
    setHasChanges(true);
  }, [segments, onChange]);

  // 刪除段落
  const handleDelete = useCallback((index: number) => {
    const newSegments = segments
      .filter((_, i) => i !== index)
      .map((seg, i) => ({ ...seg, id: i + 1 }));  // 重新編號
    onChange(newSegments);
    setHasChanges(true);
  }, [segments, onChange]);

  // 合併到上一段
  const handleMergeWithPrevious = useCallback((index: number) => {
    if (index === 0) return;
    
    const prev = segments[index - 1];
    const current = segments[index];
    const merged: TranscriptionSegment = {
      id: prev.id,
      start: prev.start,
      end: current.end,
      text: `${prev.text} ${current.text}`.trim(),
    };

    const newSegments = [
      ...segments.slice(0, index - 1),
      merged,
      ...segments.slice(index + 1),
    ].map((seg, i) => ({ ...seg, id: i + 1 }));

    onChange(newSegments);
    setHasChanges(true);
  }, [segments, onChange]);

  const handleSave = () => {
    onSave?.();
    setHasChanges(false);
  };

  if (segments.length === 0) {
    return (
      <div className="border-2 border-dashed border-gray-400 rounded-xl p-8 text-center">
        <p className="text-gray-500">尚無字幕內容</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* 工具列 */}
      <div className="flex justify-between items-center bg-white border-2 border-black rounded-xl p-3 shadow-[2px_2px_0_#1A1A1A]">
        <div className="flex items-center gap-3">
          <div className="px-3 py-1 bg-[#6FEDD6] border-2 border-black rounded-full text-xs font-bold">
            共 {segments.length} 段
          </div>
          {hasChanges && (
            <div className="flex items-center gap-1.5 text-sm font-bold text-[#FF6B9D]">
              <div className="w-2 h-2 rounded-full bg-[#FF6B9D] animate-pulse" />
              未儲存變更
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <FormatExporter segments={segments} filename={filename} disabled={disabled} />
          {onSave && (
            <button
              onClick={handleSave}
              disabled={disabled || saving || !hasChanges}
              className="
                flex items-center gap-2 px-6 py-2
                bg-[#FF6B9D] text-black font-bold
                border-2 border-black rounded-lg
                hover:translate-x-0.5 hover:translate-y-0.5
                hover:shadow-[4px_4px_0_#1A1A1A]
                active:translate-x-1 active:translate-y-1 active:shadow-none
                transition-all duration-100
                disabled:bg-gray-200 disabled:text-gray-400 disabled:border-gray-300 disabled:shadow-none
                disabled:hover:translate-x-0 disabled:hover:translate-y-0 disabled:cursor-not-allowed
              "
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              儲存
            </button>
          )}
        </div>
      </div>

      {/* 段落列表 */}
      <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto pr-2">
        {segments.map((segment, index) => (
          <SegmentRow
            key={segment.id}
            segment={segment}
            onChange={(updates) => handleSegmentChange(index, updates)}
            onDelete={segments.length > 1 ? () => handleDelete(index) : undefined}
            onMergeWithPrevious={index > 0 ? () => handleMergeWithPrevious(index) : undefined}
            isFirst={index === 0}
            disabled={disabled || saving}
          />
        ))}
      </div>
    </div>
  );
}
